import { useCallback, useEffect, useState } from 'react';
import {
  operatorConsole,
  type ListWebhookDeliveriesParams,
  type WebhookDelivery,
  type WebhookDeliveryStatus,
  type WebhookEndpoint,
  type WebhookEventInfo,
} from '../../lib/api/operatorConsole';
import { backendMessage } from '../TestPlayers/backendMessage';
import { DeliveriesTable } from './DeliveriesTable';
import { EndpointForm } from './EndpointForm';
import { EndpointRow } from './EndpointRow';

const POLL_INTERVAL_MS = 1500;
const POLL_ATTEMPTS = 14; // ~21s — past the delivery timeout with room to spare

/**
 * Where we notify you, and what we sent. Separate from Integration on purpose:
 * that page configures the wallet callbacks, which are a synchronous money
 * protocol; this one is fire-and-forget notification whose only contract is a
 * 2xx.
 */
export function WebhooksPage() {
  const [endpoints, setEndpoints] = useState<WebhookEndpoint[]>([]);
  const [availableEvents, setAvailableEvents] = useState<WebhookEventInfo[]>([]);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [status, setStatus] = useState<WebhookDeliveryStatus | ''>('');
  const [loadingEndpoints, setLoadingEndpoints] = useState(true);
  const [loadingDeliveries, setLoadingDeliveries] = useState(true);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadEndpoints = useCallback(async () => {
    try {
      setLoadingEndpoints(true);
      const data = await operatorConsole.listWebhookEndpoints();
      setEndpoints(data.webhook_endpoints);
      setAvailableEvents(data.available_events);
    } catch (err) {
      setProblem(`Failed to load endpoints: ${backendMessage(err) ?? 'unknown error'}`);
    } finally {
      setLoadingEndpoints(false);
    }
  }, []);

  const loadDeliveries = useCallback(async () => {
    try {
      setLoadingDeliveries(true);
      const params: ListWebhookDeliveriesParams = { per: 25 };
      if (status) params.status = status;
      const data = await operatorConsole.listWebhookDeliveries(params);
      setDeliveries(data.webhook_deliveries);
    } catch (err) {
      setProblem(`Failed to load deliveries: ${backendMessage(err) ?? 'unknown error'}`);
    } finally {
      setLoadingDeliveries(false);
    }
  }, [status]);

  useEffect(() => {
    void loadEndpoints();
  }, [loadEndpoints]);

  useEffect(() => {
    void loadDeliveries();
  }, [loadDeliveries]);

  function flashSuccess(message: string) {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 3000);
  }

  // Sending happens in a worker, so the answer arrives on the delivery record
  // rather than in the response. Poll it until it stops being `pending`, then
  // report; if it is still pending after this long, something is queued up
  // behind other work and the log below is the honest place to look.
  async function watchDelivery(id: number, label: string) {
    for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt += 1) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

      const delivery = await operatorConsole.getWebhookDelivery(id);
      if (delivery.status === 'pending') continue;

      if (delivery.status === 'succeeded') {
        flashSuccess(`${label} delivered (HTTP ${delivery.last_response_status ?? '2xx'})`);
      } else {
        setProblem(`${label} was not accepted: ${delivery.last_error ?? delivery.status}`);
      }
      return;
    }

    setProblem(`${label} is still being sent. It will show up in the log below when it lands.`);
  }

  async function handleCreate(endpoint: {
    url: string;
    description?: string;
    event_types: string[];
  }): Promise<boolean> {
    setProblem(null);
    try {
      await operatorConsole.createWebhookEndpoint(endpoint);
      flashSuccess('Endpoint added');
      await loadEndpoints();
      return true;
    } catch (err) {
      setProblem(backendMessage(err) ?? 'Could not add this endpoint.');
      return false;
    }
  }

  async function handleSave(
    endpoint: WebhookEndpoint,
    eventTypes: string[],
  ): Promise<boolean> {
    setProblem(null);
    setBusy(true);
    try {
      await operatorConsole.updateWebhookEndpoint(endpoint.id, { event_types: eventTypes });
      flashSuccess('Subscriptions updated');
      await loadEndpoints();
      return true;
    } catch (err) {
      setProblem(backendMessage(err) ?? 'Could not change the subscriptions.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  function handleToggleStatus(endpoint: WebhookEndpoint) {
    setProblem(null);
    setBusy(true);
    operatorConsole
      .updateWebhookEndpoint(endpoint.id, {
        status: endpoint.status === 'paused' ? 'active' : 'paused',
      })
      .then(() => {
        flashSuccess(endpoint.status === 'paused' ? 'Endpoint resumed' : 'Endpoint paused');
        return loadEndpoints();
      })
      .catch((err: unknown) => {
        setProblem(backendMessage(err) ?? 'Could not change this endpoint.');
      })
      .finally(() => setBusy(false));
  }

  function handleTest(endpoint: WebhookEndpoint) {
    setProblem(null);
    setBusy(true);
    operatorConsole
      .testWebhookEndpoint(endpoint.id)
      .then((res) => watchDelivery(res.webhook_delivery.id, 'Test event'))
      .then(() => Promise.all([loadEndpoints(), loadDeliveries()]))
      .catch((err: unknown) => {
        setProblem(backendMessage(err) ?? 'Could not send the test event.');
      })
      .finally(() => setBusy(false));
  }

  function handleDelete(endpoint: WebhookEndpoint) {
    const confirmed = window.confirm(
      `Stop sending events to ${endpoint.url}? Deliveries already recorded stay in the log.`,
    );
    if (!confirmed) return;

    setProblem(null);
    setBusy(true);
    operatorConsole
      .deleteWebhookEndpoint(endpoint.id)
      .then(() => {
        flashSuccess('Endpoint removed');
        return loadEndpoints();
      })
      .catch((err: unknown) => {
        setProblem(backendMessage(err) ?? 'Could not remove this endpoint.');
      })
      .finally(() => setBusy(false));
  }

  function handleResend(delivery: WebhookDelivery) {
    setProblem(null);
    setBusy(true);
    operatorConsole
      .resendWebhookDelivery(delivery.id)
      .then((res) => watchDelivery(res.webhook_delivery.id, 'The event'))
      .then(() => loadDeliveries())
      .catch((err: unknown) => {
        setProblem(backendMessage(err) ?? 'Could not resend this event.');
      })
      .finally(() => setBusy(false));
  }

  return (
    <div className="p-6">
      <div className="mb-5 flex items-baseline justify-between gap-4">
        <div>
          <h1 className="text-[20px] font-semibold text-[var(--co-text)]">Webhooks</h1>
          <p className="max-w-2xl text-[12px] text-[var(--co-muted)]">
            Tell us where to notify you when something happens on our side. Each request is signed
            with your API secret exactly like the wallet callbacks, and we only need a 2xx back — we
            never read your reply. Failures are retried for about half a day.
          </p>
        </div>
        {success && (
          <span className="rounded bg-[var(--co-success)] px-2 py-1 text-[11px] font-medium text-white">
            {success}
          </span>
        )}
      </div>

      {problem && (
        <div
          role="alert"
          className="mb-5 max-w-3xl rounded-xl px-4 py-3 text-[12px]"
          style={{
            color: 'var(--co-error)',
            backgroundColor: 'color-mix(in srgb, var(--co-error) 10%, transparent)',
          }}
        >
          {problem}
        </div>
      )}

      <EndpointForm availableEvents={availableEvents} onSubmit={handleCreate} />

      <div className="mb-8 max-w-3xl overflow-hidden rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)]">
        {loadingEndpoints ? (
          <p className="p-4 text-[12px] text-[var(--co-muted)]">Loading…</p>
        ) : endpoints.length === 0 ? (
          <p className="p-4 text-[12px] text-[var(--co-muted)]">
            No endpoints yet. Add one above to start receiving events.
          </p>
        ) : (
          <ul>
            {endpoints.map((endpoint) => (
              <EndpointRow
                key={endpoint.id}
                endpoint={endpoint}
                availableEvents={availableEvents}
                busy={busy}
                onSave={handleSave}
                onToggleStatus={handleToggleStatus}
                onTest={handleTest}
                onDelete={handleDelete}
              />
            ))}
          </ul>
        )}
      </div>

      <DeliveriesTable
        deliveries={deliveries}
        endpoints={endpoints}
        loading={loadingDeliveries}
        busy={busy}
        status={status}
        onStatusChange={setStatus}
        onResend={handleResend}
      />
    </div>
  );
}
